'use client';

import { useEffect, useState } from 'react';
import { readSeedingWatermark, type SeedingUnviewedWatermark } from './seedingUnviewedWatermark';

/**
 * blue dot watermark 의 mount 게이트 + localStorage 동기화 상태.
 *
 * 진입(mount) 시 1회 읽고, **재가시(visibilitychange→visible) / 포커스 / 타 문서 storage 이벤트**
 * 마다 재독한다. 29CM 은 "제안/신청 목록"을 새 웹뷰(openWebview)로 열어 me 페이지가 **마운트된
 * 채로 남는다**(CMQA-7180 동류). 그 목록 웹뷰에서 `markAsViewed` 가 localStorage 를 갱신해도,
 * me 페이지 hook 인스턴스의 `seen` 은 최초 1회 읽은 값에 머물러 "확인했는데도 dot 잔존"이 발생했다.
 * 재가시마다 재독해 localStorage 와 동기화하면, 목록을 보고 돌아온 시점에 dot 이 사라진다.
 * (무신사/web 은 router.push 로 unmount→재mount 되어 자연 재독되지만, 이 동기화는 양쪽 모두 안전.)
 *
 * @returns mounted (SSR 하이드레이션 가드 + query enable), seen (현재 watermark), setSeen (markAsViewed 용)
 */
export const useSeedingWatermark = (storageKey: string) => {
  const [mounted, setMounted] = useState(false);
  const [seen, setSeen] = useState<SeedingUnviewedWatermark | null>(null);

  useEffect(() => {
    setMounted(true);
    const sync = () => setSeen(readSeedingWatermark(storageKey));
    sync();
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', sync);
    window.addEventListener('storage', sync);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', sync);
      window.removeEventListener('storage', sync);
    };
  }, [storageKey]);

  return { mounted, seen, setSeen };
};
